import type { TenantFeatures } from "breuer-supercharged-sdk";

/**
 * Pure helpers for the module editor (Admin → Mandanten). Only the system admin
 * switches modules; the values are the `tenant_features` DB enum (via the SDK).
 */

export interface TenantModule {
  key: string;
  label: string;
  /** Compact name for the tenant table. */
  shortLabel: string;
  description: string;
  /** `null` = always on, not part of `tenant_features` (cannot be switched). */
  feature: TenantFeatures | null;
}

// Record<TenantFeatures, …> makes `tsc` fail when a new enum value has no label yet.
const SWITCHABLE: Record<TenantFeatures, Omit<TenantModule, "feature" | "key">> = {
  "stücklisten_extractor": {
    label: "PDF2BVBS (Stücklisten-Extractor)",
    shortLabel: "PDF2BVBS",
    description: "Stück- und Biegelisten aus PDF-Dokumenten auslesen und als BVBS exportieren",
  },
  offer_generation: {
    label: "Angebote erstellen",
    shortLabel: "Angebote",
    description: "Angebote aus Anfragen erstellen, kalkulieren und versenden",
  },
  steel_mind: {
    label: "SteelMind",
    shortLabel: "SteelMind",
    description: "Intelligente Stahlbearbeitung und -optimierung",
  },
  order_inbox: {
    label: "Bestelleingang",
    shortLabel: "Bestelleingang",
    description: "Eingehende Bestellungen per E-Mail empfangen, prüfen und an die AV weiterleiten",
  },
};

const SWITCHABLE_ORDER: TenantFeatures[] = ["stücklisten_extractor", "offer_generation", "steel_mind", "order_inbox"];

export const TENANT_MODULES: readonly TenantModule[] = [
  ...SWITCHABLE_ORDER.map((feature) => ({ key: feature, feature, ...SWITCHABLE[feature] })),
  {
    key: "arbeitsvorbereitung",
    label: "Arbeitsvorbereitung",
    shortLabel: "Arbeitsvorbereitung",
    description: "AV-Dashboard für Bestellungen – ist für jeden Mandanten aktiv",
    feature: null,
  },
];

export function isModuleEnabled(features: readonly string[] | null | undefined, module: TenantModule): boolean {
  if (module.feature === null) return true;
  return features?.includes(module.feature) ?? false;
}

/** New feature list with `feature` switched on/off, deduplicated and in editor order (unknown keys are kept at the end). */
export function setTenantFeature(
  features: readonly TenantFeatures[] | null | undefined,
  feature: TenantFeatures,
  enabled: boolean,
): TenantFeatures[] {
  const next = new Set(features ?? []);
  if (enabled) next.add(feature);
  else next.delete(feature);
  const known = SWITCHABLE_ORDER.filter((f) => next.has(f));
  const unknown = [...next].filter((f) => !SWITCHABLE_ORDER.includes(f));
  return [...known, ...unknown];
}

export function sameFeatureSet(
  a: readonly string[] | null | undefined,
  b: readonly string[] | null | undefined,
): boolean {
  const left = new Set(a ?? []);
  const right = new Set(b ?? []);
  return left.size === right.size && [...left].every((f) => right.has(f));
}

/** Short labels of the switched-on modules, for the tenant table. */
export function enabledModuleLabels(features: readonly string[] | null | undefined): string[] {
  return TENANT_MODULES.filter((m) => m.feature !== null && isModuleEnabled(features, m)).map((m) => m.shortLabel);
}
